import { useState } from "react";
import { useAxios } from "../../../utils/useAxios";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { ERROR_MESSAGES } from "../../../constant/constant";

/**
 * @typedef {Object} CreatorType
 * @property {number} id - The creator's ID
 * @property {string} firstName - The creator's first name
 * @property {string} lastName - The creator's last name
 * @property {string} email - The creator's email
 * @property {number} role_id - The creator's role ID
 * @property {string} teacher_code - The creator's teacher code
 */

/**
 * @typedef {Object} LessonDataType
 * @property {number} id - The lesson's ID
 * @property {string} title - The title of the lesson
 * @property {string} tenant_id - The tenant ID
 * @property {string} description - The description of the lesson
 * @property {number} created_by - The ID of the user who created the lesson
 * @property {string} created_at - The timestamp when the lesson was created
 * @property {string} updated_at - The timestamp when the lesson was updated
 * @property {string} lesson_code - The code of the lesson
 * @property {number} quest_count - The count of quests in the lesson
 * @property {number} classroom_count - The count of classrooms in the lesson
 * @property {CreatorType} creator - The creator of the lesson
 */

/**
 * @typedef {import("../../../common/types/types").PaginationMeta} PaginationMeta
 */

export const useGetAllLessons = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lessons, setLessons] = useState(
    /** @type {LessonDataType[] | null | undefined} */(undefined),
  );
  const [meta, setMeta] = useState(/** @type {PaginationMeta | null} */(null));
  const { axiosInstance } = useAxios();

  /**
   * Fetch lessons with pagination, filtering & sorting
   * @async
   * @param {Object} params
   * @param {number} [params.page] - Page number for pagination
   * @param {number} [params.per_page] - Number of items per page
   * @param {Object} [params.filters] - Filters in key:value format
   * @param {string} [params.sort] - Sorting (e.g. "title" for ascending or "-title" for descending)
   * @returns {Promise<void>}
   */
  const getAllLessons = async ({
    page = 1,
    per_page = 10,
    filters = {},
    sort = "",
  } = {}) => {
    setLoading(true);
    setError("");
    try {
      // --- Build Query Params ---
      const query = new URLSearchParams();

      // Pagination
      query.append("page", page);
      query.append("per_page", per_page);

      // Filters
      Object.keys(filters).forEach((field) => {
        if (filters[field] !== "" && filters[field] !== undefined) {
          query.append(`filter[${field}]`, filters[field]);
        }
      });

      // Sorting
      if (sort) query.append("sort", sort);

      const url = `${TENANT_DOMAIN}/lessons?${query.toString()}`;

      const response = await axiosInstance.get(url);
      setLessons(response?.data?.data || []);
      setMeta(response?.data?.meta || null);

    } catch (error) {

      setLessons(null);
      setMeta(null);
      if (error?.response?.status === 500) {
        setError(ERROR_MESSAGES.INTERNAL_SERVER);
      } else {
        setError(error?.response?.data?.message || ERROR_MESSAGES.UNKNOWN);
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    lessons,
    meta,
    loading,
    error,
    getAllLessons,
  };
};

